/**
 * WorkspaceHero — shared aspect. The dark forest hero strip at the
 * top of a per-program workspace: amber kicker line + uppercase
 * eyebrow (company · year), Cormorant program title, status chip +
 * meta items (team size, closes-on date), and a small donut +
 * "case progress" caption on the right.
 *
 * Used by /brand Aspect 25 AND by the real CompactWorkspaceHero
 * (which adds palm scenery and the radial-gradient amber wash on
 * top — wrapper stays in the consumer).
 *
 * Consumer pre-formats kicker, dateLabel, and progressCaption.
 */
import { Platform, Text, View, type ViewStyle } from "react-native";
import Svg, { Circle, G, Path } from "react-native-svg";

import { COLORS, RADII, SPACING, STROKES } from "../../lib/brand";
import type { PaneTheme } from "../brand/paneThemes";

export type WorkspaceHeroChip = {
  label: string;
  tone: "sage" | "amber" | "grey";
};

export type WorkspaceHeroMetaItem = {
  icon: "users" | "calendar";
  label: string;
};

export type WorkspaceHeroProps = {
  theme: PaneTheme;
  kicker: string;
  title: string;
  chip: WorkspaceHeroChip;
  meta: WorkspaceHeroMetaItem[];
  /** 0..1 — drives the donut fill + percentage label. */
  progressPct: number;
  progressCaption: string;
  nextDueLabel?: string;
};

export function WorkspaceHero({
  theme,
  kicker,
  title,
  chip,
  meta,
  progressPct,
  progressCaption,
  nextDueLabel,
}: WorkspaceHeroProps) {
  const isWeb = Platform.OS === "web";
  return (
    <View
      style={[
        {
          backgroundColor: theme.sidebarBg,
          paddingVertical: 28,
          paddingHorizontal: 36,
          flexDirection: "row",
          alignItems: "center",
          gap: 32,
          flexWrap: "wrap",
          overflow: "hidden",
          position: "relative",
          borderBottomWidth: STROKES.hairline,
          borderBottomColor: theme.accentHalo,
        },
        isWeb
          ? ({
              backgroundImage:
                "radial-gradient(ellipse 800px 420px at 80% 30%, rgba(181,135,58,0.10) 0%, transparent 60%)",
            } as unknown as ViewStyle)
          : null,
      ]}
    >
      <View style={{ flex: 1, minWidth: 280, gap: 8 }}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
          <View style={{ width: 28, height: 1, backgroundColor: theme.accent }} />
          <Text
            style={{
              fontFamily: theme.bodyBoldFont,
              fontSize: 10,
              letterSpacing: 2.2,
              color: theme.accent,
            }}
            numberOfLines={1}
          >
            {kicker}
          </Text>
        </View>
        <Text
          style={{
            fontFamily: theme.displayMediumFont,
            fontSize: 30,
            lineHeight: 32,
            letterSpacing: -0.15,
            color: COLORS.LINEN.hex,
          }}
          numberOfLines={2}
        >
          {title}
        </Text>
        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 16,
            marginTop: 4,
          }}
        >
          <StatusChip theme={theme} chip={chip} />
          {meta.map((m, i) => (
            <MetaItem key={`${m.icon}-${i}`} theme={theme} item={m} />
          ))}
        </View>
      </View>

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 18,
          flexShrink: 0,
        }}
      >
        <SmallDonut theme={theme} pct={progressPct} size={92} />
        <View style={{ minWidth: 130 }}>
          <Text
            style={{
              fontFamily: theme.bodyBoldFont,
              fontSize: 9.5,
              letterSpacing: 2,
              color: theme.accent,
              textTransform: "uppercase",
            }}
          >
            Case progress
          </Text>
          <Text
            style={{
              marginTop: 4,
              fontFamily: theme.displayMediumFont,
              fontSize: 14,
              lineHeight: 18,
              color: COLORS.LINEN.hex,
            }}
          >
            {progressCaption}
          </Text>
          {nextDueLabel ? (
            <Text
              style={{
                marginTop: 4,
                fontFamily: theme.bodyFont,
                fontSize: 11,
                color: "rgba(247,244,238,0.72)",
              }}
            >
              {nextDueLabel}
            </Text>
          ) : null}
        </View>
      </View>
    </View>
  );
}

function StatusChip({ theme, chip }: { theme: PaneTheme; chip: WorkspaceHeroChip }) {
  const cfg = (() => {
    if (chip.tone === "sage") return {
      color: COLORS.SAGE.hex,
      bg: theme.successTintSoft,
      border: theme.successBorderSoft,
    };
    if (chip.tone === "amber") return {
      color: theme.accent,
      bg: theme.accentTintSoft,
      border: theme.accentBorderHover,
    };
    return {
      color: theme.textOnSidebarDim,
      bg: theme.surfaceDividerFaint,
      border: theme.surfaceDividerStrong,
    };
  })();
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingHorizontal: 11,
        paddingVertical: SPACING.xxs,
        borderRadius: RADII.full,
        borderWidth: STROKES.hairline,
        backgroundColor: cfg.bg,
        borderColor: cfg.border,
      }}
    >
      <View
        style={{
          width: 6,
          height: 6,
          borderRadius: RADII.full,
          backgroundColor: cfg.color,
        }}
      />
      <Text
        style={{
          fontFamily: theme.bodyBoldFont,
          fontSize: 9.5,
          letterSpacing: 1.5,
          color: cfg.color,
          textTransform: "uppercase",
        }}
      >
        {chip.label}
      </Text>
    </View>
  );
}

function MetaItem({ theme, item }: { theme: PaneTheme; item: WorkspaceHeroMetaItem }) {
  return (
    <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
      <MetaIcon icon={item.icon} color={theme.textOnSidebarDim} />
      <Text
        style={{
          fontFamily: theme.bodyFont,
          fontSize: 12,
          color: theme.textOnSidebarDim,
        }}
      >
        {item.label}
      </Text>
    </View>
  );
}

function MetaIcon({ icon, color }: { icon: "users" | "calendar"; color: string }) {
  if (icon === "users") {
    return (
      <Svg width={13} height={13} viewBox="0 0 14 14" fill="none">
        <Circle cx={5} cy={4} r={2.2} stroke={color} strokeWidth={1.1} />
        <Circle cx={10} cy={5} r={1.8} stroke={color} strokeWidth={1.1} />
        <Path d="M1 12c0-2.2 1.8-4 4-4s4 1.8 4 4" stroke={color} strokeWidth={1.1} strokeLinecap="round" />
        <Path d="M10 8c1.7 0 3 1.1 3 2.5" stroke={color} strokeWidth={1.1} strokeLinecap="round" />
      </Svg>
    );
  }
  return (
    <Svg width={13} height={13} viewBox="0 0 14 14" fill="none">
      <Path
        d="M2.5 3.5h9a1 1 0 011 1v7a1 1 0 01-1 1h-9a1 1 0 01-1-1v-7a1 1 0 011-1z"
        stroke={color}
        strokeWidth={1.1}
      />
      <Path d="M2.5 6h10" stroke={color} strokeWidth={1.1} />
      <Path d="M5 2v3M9 2v3" stroke={color} strokeWidth={1.1} strokeLinecap="round" />
    </Svg>
  );
}

function SmallDonut({
  theme,
  pct,
  size,
}: {
  theme: PaneTheme;
  pct: number;
  size: number;
}) {
  const stroke = 7;
  const inset = stroke / 2 + 2;
  const radius = (size - inset * 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(1, pct));
  const dashOffset = circumference * (1 - clamped);
  const pctLabel = `${Math.round(clamped * 100)}%`;

  return (
    <View style={{ width: size, height: size }}>
      <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={theme.surfaceDividerFaint}
          strokeWidth={stroke}
        />
        <G rotation={-90} origin={`${size / 2}, ${size / 2}`}>
          <Circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={clamped === 0 ? theme.surfaceDividerStrong : theme.accent}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
          />
        </G>
      </Svg>
      <View
        pointerEvents="none"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: size,
          height: size,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text
          style={{
            fontFamily: theme.displayMediumFont,
            fontSize: 24,
            color: COLORS.LINEN.hex,
          }}
        >
          {pctLabel}
        </Text>
      </View>
    </View>
  );
}
